import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { formatDateTime } from "@buildpy/core/lib/format";
import { ARTICLE_BODY_MAX, ARTICLE_TITLE_MAX } from "@buildpy/core/modules/process-standard/proposal/types";
import { PRICE_INFO_BAN_TEXT, type ProcessStandardArticle } from "@buildpy/core/modules/process-standard/types";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";

import { editArticle } from "@/api/actions/process-standard";
import Field from "@/components/field";

import { useStandardAction } from "./use-standard-action";

// 「마지막 수정 · 회사명 · 시각」 — 아무도 안 고친 시드 글이면 null.
export const lastEditText = (article: Pick<ProcessStandardArticle, "updated_by_org_name" | "updated_at">): string | null =>
  article.updated_by_org_name ? `마지막 수정 · ${article.updated_by_org_name} · ${formatDateTime(article.updated_at)}` : null;

// 시공법 글 — 누구나 바로 고치고 저장 즉시 반영(#384) · 화면이 본 제목·본문을 같이 보내 그 사이 고친 글은 액션이 막는다.
const ArticleBlock = ({ article }: { article: ProcessStandardArticle }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(article.title);
  const [body, setBody] = useState(article.body);
  const { pending, run } = useStandardAction();
  const edited = lastEditText(article);

  const open = () => {
    setTitle(article.title);
    setBody(article.body);
    setEditing(true);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    run(
      () => editArticle({ article_id: article.id, seen: { title: article.title, body: article.body }, title, body }),
      () => {
        setEditing(false);
        toast.success("시공법 글을 고쳤습니다");
      }
    );
  };

  if (editing) {
    return (
      <form className="mt-3 flex flex-col gap-y-3" data-testid={`article-edit-${article.code}`} onSubmit={onSubmit}>
        <fieldset className="flex flex-col gap-y-3" disabled={pending}>
          <Field label="제목">
            <Input maxLength={ARTICLE_TITLE_MAX} name="title" onChange={(e) => setTitle(e.target.value)} value={title} />
          </Field>
          <Field label="본문">
            <Textarea className="min-h-40 text-sm" maxLength={ARTICLE_BODY_MAX} onChange={(e) => setBody(e.target.value)} value={body} />
          </Field>
        </fieldset>
        <p className="text-xs text-muted-foreground">저장하면 모든 시공사 화면에 바로 반영되고, 우리 회사 이름이 마지막 수정으로 남습니다. {PRICE_INFO_BAN_TEXT}</p>
        <div className="flex justify-end gap-2">
          <Button disabled={pending} onClick={() => setEditing(false)} size="sm" type="button" variant="ghost">
            취소
          </Button>
          <Button disabled={pending} size="sm" type="submit">
            저장
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="mt-3 rounded-md bg-muted px-3 py-2" data-testid={`article-${article.code}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-medium">{article.title}</h3>
        <Button onClick={open} size="compact" variant="ghost-muted">
          고치기
        </Button>
      </div>
      <p className="mt-1 whitespace-pre-line break-words text-sm text-muted-foreground">{article.body}</p>
      {edited && <p className="mt-2 text-xs text-muted-foreground">{edited}</p>}
    </div>
  );
};

export default ArticleBlock;
